import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { GraduationCap, User } from 'lucide-react';
import API_BASE_URL from '../config/api';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [shareCode, setShareCode] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  // Vérifier si l'utilisateur est déjà connecté
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      navigate('/my-tests');
    }
  }, [navigate]);

  const handleStudentJoin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (shareCode.trim().length !== 6) {
      setError('Veuillez entrer un code à 6 caractères');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/tests/by-code/${shareCode.toUpperCase()}`,
        { method: 'GET' }
      );

      if (!response.ok) {
        setError('Test introuvable. Veuillez vérifier le code.');
        setLoading(false);
        return;
      }

      const data = await response.json();

      // Naviguer vers la page de réponse étudiant
      navigate(`/student-answer-test/${data.test.id}`);

    } catch (err) {
      setError('Erreur de connexion au serveur');
      setLoading(false);
    }
  };

  const handleTeacherLogin = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center px-4">
      <div className="max-w-4xl w-full">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Carte Étudiant */}
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <div className="flex items-center justify-center mb-6">
              <div className="bg-green-100 p-4 rounded-full">
                <GraduationCap className="w-12 h-12 text-green-600" />
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-800 text-center mb-2">
              Je suis étudiant
            </h2>
            <p className="text-gray-600 text-center mb-6">
              Entrez votre code de test
            </p>

            <form onSubmit={handleStudentJoin}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Code du test
                </label>
                <input
                  type="text"
                  value={shareCode}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                    if (value.length <= 6) {
                      setShareCode(value);
                      setError('');
                    }
                  }}
                  placeholder="XY4K9P"
                  maxLength={6}
                  className="w-full px-4 py-3 text-center text-2xl font-mono border-2 border-gray-300 rounded-lg focus:border-green-500 focus:outline-none uppercase tracking-widest"
                />
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || shareCode.length !== 6}
                className={`w-full py-3 rounded-lg font-semibold transition ${
                  loading || shareCode.length !== 6
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700 text-white'
                }`}
              >
                {loading ? 'Chargement...' : 'Rejoindre le test'}
              </button>
            </form>
          </div>

          {/* Carte Enseignant */}
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <div className="flex items-center justify-center mb-6">
              <div className="bg-blue-100 p-4 rounded-full">
                <User className="w-12 h-12 text-blue-600" />
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-800 text-center mb-2">
              Je suis enseignant
            </h2>
            <p className="text-gray-600 text-center mb-6">
              Gérez et créez vos tests
            </p>

            <div className="flex flex-col items-center justify-center h-40">
              <button
                onClick={handleTeacherLogin}
                className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition"
              >
                Connexion enseignant
              </button>

              <p className="text-sm text-gray-500 mt-4">
                Pas de compte ?{' '}
                <a href="/signup" className="text-blue-600 hover:underline">
                  S'inscrire
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
